import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import ExpandableSection from '@components/animations/ExpandableSection';
import TertiaryButton from '@components/buttons/TertiaryButton';
import ArticleCard from '@components/cards/ArticleCard';
import styles from '@components/carePlan/ExpertCarePlan.module.css';
import { Section } from '@components/carePlan/GoalsManager';
import ItemBrowser from '@components/carePlan/ItemBrowser';
import EmployerBenefitCard from '@components/explore/EmployerBenefitCard';
import SearchInput from '@components/fieldsNative/SearchInput';
import Heading2 from '@components/typography/Heading2';
import useCreateGoalItem from '@hooks/mutations/useCreateGoalItem';
import useRemoveGoalItem from '@hooks/mutations/useRemoveGoalItem';
import useOpener from '@hooks/useOpener';
import useSearchArticles from '@hooks/useSearchArticles';
import useSearchEmployerBenefits from '@hooks/useSearchEmployerBenefits';
import { GetJourneyWithPlanQuery, GoalItemTypeEnum } from '@typing/Generated';
import { sortByTitle } from '@utils/modelUtils';

type Journey = GetJourneyWithPlanQuery['journey'];
type Goal = Journey['goals'][0];

type Props = {
  currentSection: Section;
  goal: Goal;
  journey: Journey;
};

const GoalsManagerMedia = ({ currentSection, goal, journey }: Props) => {
  const mediaCreateOpener = useOpener();
  const articleBrowserOpener = useOpener();
  const employerBenefitBrowserOpener = useOpener();

  const intl = useIntl();
  const [articleSearch, setArticleSearch] = useState('');
  const [employerBenefitSearch, setEmployerBenefitSearch] = useState('');

  const removeGoalItem = useRemoveGoalItem(goal);

  const handleEmployerBenefitRemove = (employerBenefitId: string) => {
    if (goal) {
      removeGoalItem({
        employerBenefitId,
        goalId: goal.id
      });
    }
  };

  const handleArticleRemove = (articleId: string) => {
    if (goal) {
      removeGoalItem({
        articleId,
        goalId: goal.id
      });
    }
  };

  const createGoalItem = useCreateGoalItem(goal);

  const searchArticles = useSearchArticles(journey.id, id =>
    createGoalItem({ articleId: id, goalId: goal?.id ?? '', itemType: GoalItemTypeEnum.ARTICLE })
  );

  const onEmployerBenefitSearch = useSearchEmployerBenefits(journey.id, id =>
    createGoalItem({ employerBenefitId: id, goalId: goal?.id ?? '', itemType: GoalItemTypeEnum.EMPLOYER_BENEFIT })
  );

  return (
    <div className={styles.goalEditorSection}>
      {currentSection === Section.ALL && !goal.draft && (
        <Heading2 color="primary300">
          <FormattedMessage id={`models.goal.sections.${Section.MEDIA}`} />
        </Heading2>
      )}
      <div className={styles.mediaItems}>
        {sortByTitle(goal.articles).map(article => (
          <ArticleCard article={article} journeyId={journey.id} key={article.id} onRemove={handleArticleRemove} />
        ))}
        {goal.employerBenefits.map(employerBenefit => (
          <EmployerBenefitCard
            employerBenefit={employerBenefit}
            journeyId={journey.id}
            key={employerBenefit.id}
            layout="vertical"
            onRemove={handleEmployerBenefitRemove}
          />
        ))}
      </div>

      <div className={styles.spaceBetween + ' ' + styles.topSpacing}>
        <div>
          <TertiaryButton
            icon={mediaCreateOpener.isOpen ? 'chevronUp' : 'chevronDown'}
            iconPosition="end"
            onClick={mediaCreateOpener.toggle}
            textKey="models.goal.media.add"
          />
        </div>
      </div>
      <ExpandableSection isOpen={mediaCreateOpener.isOpen}>
        <div>
          <div className={styles.topSpacing}>
            <SearchInput
              error={''}
              initialValue={articleSearch}
              name="search"
              noSpacing
              onSearch={searchArticles}
              placeholder={intl.formatMessage({ id: 'pages.carePlan.base.articles.searchPlaceholder' })}
              setValue={setArticleSearch}
            />
          </div>
          <ItemBrowser
            addedIds={goal.articles.map(a => a.id)}
            handleAdd={id => {
              createGoalItem({ articleId: id, goalId: goal.id, itemType: GoalItemTypeEnum.ARTICLE });
            }}
            handleRemove={handleArticleRemove}
            itemType={GoalItemTypeEnum.ARTICLE}
            journeyId={journey.id}
            opener={articleBrowserOpener}
            skipCloseOnSubmit
          />
          <div className={styles.topSpacing}>
            <SearchInput
              error={''}
              initialValue={employerBenefitSearch}
              name="marketplaceSearch"
              noSpacing
              onSearch={onEmployerBenefitSearch}
              placeholder={intl.formatMessage({ id: 'pages.carePlan.base.employerBenefit.searchPlaceholder' })}
              setValue={setEmployerBenefitSearch}
            />
          </div>
          <ItemBrowser
            addedIds={goal.employerBenefits.map(eb => eb.id)}
            handleAdd={id => {
              createGoalItem({
                employerBenefitId: id,
                goalId: goal.id,
                itemType: GoalItemTypeEnum.EMPLOYER_BENEFIT
              });
            }}
            handleRemove={handleEmployerBenefitRemove}
            itemType={GoalItemTypeEnum.EMPLOYER_BENEFIT}
            journeyId={journey.id}
            opener={employerBenefitBrowserOpener}
            skipCloseOnSubmit
          />
        </div>
      </ExpandableSection>
    </div>
  );
};

export default GoalsManagerMedia;
